import { useEffect, useMemo, useState } from "react";

import AttachedCloudIntegrationsSection from "components/AttachCloud/Details/CloudIntegrationsSection";
import AttachCloudForm from "components/AttachCloud/Form";
import { AttachCloudEntityType } from "components/AttachCloud/Form/types";
import DocumentationButton from "components/DocumentationButton";
import FormLoading from "components/form/components/loading";
import useErrorHandlerSourceCode from "components/Forms/SourceCode/useErrorHandlerSourceCode";
import { EmptystateCloudColored } from "components/icons/generated";
import Banner from "ds/components/Banner";
import Box from "ds/components/Box";
import EmptyState from "ds/components/EmptyState";
import FullScreenModalBody from "ds/components/FullScreenModal/Body";
import FullScreenModalBodyHeader from "ds/components/FullScreenModal/BodyHeader";
import useTypedContext from "hooks/useTypedContext";
import { CLOUD_INTEGRATIONS } from "shared/CloudIntegration/types";
import useAttachableCloudIntegrations from "shared/CloudIntegration/useAttachableCloudIntegrations";
import { getDocsUrl } from "utils/getDocsUrl";

import { ModuleFormContext } from "../context";
import NewModuleFooter from "../Footer";
import { ModuleCreationWizardStep } from "../types";
import NewModuleAttachCloudDocumentation from "./Documentation";
import useAttachedCloudIntegration from "./hooks/useAttachedCloudIntegration";

const refetchQueries = ["GetAttachedModuleIntegrations"];

const NewModuleAttachCloud = () => {
  const { setAccountOrAuthErrorContent, createdModuleId, formData } =
    useTypedContext(ModuleFormContext);
  const moduleLabels = useMemo(
    () => formData[ModuleCreationWizardStep.Details].labels.map((label) => label.value),
    [formData]
  );

  const {
    loading: integrationLoading,
    manuallyAttachedIntegrations,
    autoAttachedIntegrations,
    refetch: refetchAttachedCloudIntegrations,
  } = useAttachedCloudIntegration(createdModuleId);

  const {
    loading: cloudConfigLoading,
    cloudConfig,
    attachableAzureIntegrations,
    attachableAwsIntegrations,
    hasData,
    refetch,
    refetching,
    error,
  } = useAttachableCloudIntegrations(formData[ModuleCreationWizardStep.Vcs].space);

  const [cloud, setCloud] = useState<CLOUD_INTEGRATIONS>(cloudConfig[0].type);

  useEffect(() => {
    setCloud(cloudConfig[0].type);
  }, [cloudConfig]);

  useErrorHandlerSourceCode(setAccountOrAuthErrorContent, error);

  if (cloudConfigLoading || integrationLoading) {
    return <FormLoading />;
  }

  const hasNoClouds =
    hasData &&
    !attachableAwsIntegrations.length &&
    !attachableAzureIntegrations.length &&
    (!manuallyAttachedIntegrations.length || !autoAttachedIntegrations.length);

  return (
    <>
      <FullScreenModalBody>
        <FullScreenModalBodyHeader
          title="Attach cloud integration (optional)"
          description={hasNoClouds ? undefined : "Integrate module with your cloud provider"}
        />

        {hasNoClouds ? (
          <Box direction="column" gap="large">
            <Banner variant="info">
              You can only attach integrations from the current space and parent spaces that you
              inherit from.
            </Banner>
            <EmptyState
              padding="large"
              icon={EmptystateCloudColored}
              title="You do not have any cloud integrations yet"
              caption={
                <>
                  Cloud Integrations are used to dynamically generate short-lived credentials to
                  authenticate to cloud providers (AWS/Azure/GCP currently supported). In this way
                  you avoid using static credentials and by doing so, you reduce breaches.
                  <br /> Read more in the Documentation.
                </>
              }
            >
              <DocumentationButton
                to={getDocsUrl("/integrations/cloud-providers")}
                label="Documentation"
              />
            </EmptyState>
          </Box>
        ) : (
          <>
            {manuallyAttachedIntegrations.length ? (
              <AttachedCloudIntegrationsSection
                title="Attached directly"
                integrations={manuallyAttachedIntegrations}
                cloudConfig={cloudConfig}
                stackId={createdModuleId}
                readOnly={false}
                onSuccessDetach={refetchAttachedCloudIntegrations}
                stackLabels={moduleLabels}
              />
            ) : (
              <Box padding="0 x-large" direction="column">
                <Banner variant="info">
                  You can only attach integrations from the current space and parent spaces that you
                  inherit from.
                </Banner>
                <AttachCloudForm
                  entityId={createdModuleId}
                  entityType={AttachCloudEntityType.Module}
                  hasData={hasData}
                  refetch={refetch}
                  refetching={refetching}
                  setCloud={setCloud}
                  cloud={cloud}
                  cloudConfig={cloudConfig}
                  attachableAzureIntegrations={attachableAzureIntegrations}
                  attachableAwsIntegrations={attachableAwsIntegrations}
                  refetchQueries={refetchQueries}
                />
              </Box>
            )}
            {autoAttachedIntegrations.length ? (
              <AttachedCloudIntegrationsSection
                title="Auto-attached"
                integrations={autoAttachedIntegrations}
                cloudConfig={cloudConfig}
                stackId={createdModuleId}
                readOnly={true}
                isAutoAttached={true}
                stackLabels={moduleLabels}
              />
            ) : null}
          </>
        )}
      </FullScreenModalBody>

      <NewModuleAttachCloudDocumentation cloud={cloud}>
        {({ body, link }) => (
          <NewModuleFooter
            documentationLink={link}
            documentationTitle="Cloud integration"
            documentationBody={body}
          />
        )}
      </NewModuleAttachCloudDocumentation>
    </>
  );
};

export default NewModuleAttachCloud;
